import { forwardRef, useMemo } from 'react';
import { Streamdown } from 'streamdown';

import './streamdown.css';

import CustomLink from './components/CustomLink';
import CustomSup from './components/CustomSup';
import CustomTable from './components/CustomTable';
import { useMarkdownCopyRef } from './hooks/useMarkdownCopyRef';
import {
  DEFAULT_REMARK_PLUGINS,
  DEFAULT_STATIC_REHYPE_PLUGINS,
} from './MarkdownRenderer.constants';
import { MarkdownRendererContext } from './MarkdownRenderer.context';
import { rehypeCitationLinks } from './plugins/rehype-citation-links';

import type { MarkdownRendererContextValue } from './MarkdownRenderer.context';
import type { CitationSource, StreamingCitationSources } from '../../types';
import type { StreamdownProps } from 'streamdown';
import type { PluggableList } from 'unified';

type MarkdownRendererProps = {
  content: string;
  sources?: StreamingCitationSources;
  /** Previous version's sources, used in diff mode to resolve deleted citations */
  diffSources?: StreamingCitationSources;
  onRemoveSource?: (source: CitationSource) => void;
  isRemoveSourceDisabled?: boolean;
  /** Override citation popover content rendering. Return null to use default shared rendering. */
  renderCitationContentOverride?: MarkdownRendererContextValue['renderCitationContentOverride'];
  remarkPlugins?: PluggableList;
  rehypePlugins?: PluggableList;
  remarkRehypeOptions?: StreamdownProps['remarkRehypeOptions'];
  components?: StreamdownProps['components'];
  mode?: StreamdownProps['mode'];
};

export type MarkdownRendererRef = {
  getHtmlContent: () => string;
  getPlainText: () => string;
};

/** The renderer's own components; spread them into a `components` override. */
export const DEFAULT_COMPONENTS: StreamdownProps['components'] = {
  a: CustomLink,
  sup: CustomSup,
  table: CustomTable,
};

const MarkdownRenderer = forwardRef<MarkdownRendererRef, MarkdownRendererProps>(
  (
    {
      content,
      sources = [],
      diffSources,
      onRemoveSource,
      isRemoveSourceDisabled = false,
      renderCitationContentOverride,
      remarkPlugins: remarkPluginsProp,
      rehypePlugins: rehypePluginsProp,
      remarkRehypeOptions,
      components: componentsProp,
      mode,
    },
    ref,
  ) => {
    const containerRef = useMarkdownCopyRef(ref);

    const contextValue = useMemo<MarkdownRendererContextValue>(
      () => ({
        sources,
        diffSources,
        onRemoveSource,
        isRemoveSourceDisabled,
        renderCitationContentOverride,
      }),
      [
        sources,
        diffSources,
        onRemoveSource,
        isRemoveSourceDisabled,
        renderCitationContentOverride,
      ],
    );

    const components = componentsProp ?? DEFAULT_COMPONENTS;

    const rehypePlugins = useMemo<PluggableList>(
      () =>
        rehypePluginsProp ?? [
          ...DEFAULT_STATIC_REHYPE_PLUGINS,
          [rehypeCitationLinks, { sources }],
        ],
      [rehypePluginsProp, sources],
    );

    const remarkPlugins = remarkPluginsProp ?? DEFAULT_REMARK_PLUGINS;

    return (
      <MarkdownRendererContext.Provider value={contextValue}>
        <div ref={containerRef} className="markdownRenderer">
          <Streamdown
            mode={mode}
            remarkPlugins={remarkPlugins}
            rehypePlugins={rehypePlugins}
            remarkRehypeOptions={remarkRehypeOptions}
            components={components}
          >
            {content}
          </Streamdown>
        </div>
      </MarkdownRendererContext.Provider>
    );
  },
);

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
